
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowDown, ArrowUp } from "lucide-react";
import { 
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { DataLoader } from "../components/DataLoader";
import { CharterSignatory } from "../utils/types";
import { formatDate } from "../utils/formatters";
import { SearchBar } from "./members/SearchBar";
import { DateRangeFilter } from "./members/DateRangeFilter";
import { useDataFilters } from "./hooks/useDataFilters";
import { useSortData } from "./hooks/useSortData";
import { usePagination } from "./hooks/usePagination";
import { TablePagination } from "./members/TablePagination";
import { ExportButtons } from "./signatories/ExportButtons";

export const SignatoriesTab = () => {
  const [signatories, setSignatories] = useState<CharterSignatory[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchSignatories();
  }, []);

  const fetchSignatories = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const { data, error } = await supabase
        .from('charter_signatories')
        .select('id, first_name, last_name, email, phone, created_at, signature_number')
        .order('created_at', { ascending: false });
      
      if (error) {
        throw error;
      }
      
      setSignatories(data || []);
    } catch (err) {
      console.error("Erreur lors de la récupération des signataires:", err);
      setError("Impossible de récupérer les données des signataires.");
    } finally {
      setIsLoading(false);
    }
  };

  // Filter data
  const {
    startDate,
    endDate,
    searchTerm,
    filteredData,
    handleStartDateChange,
    handleEndDateChange,
    handleSearchChange
  } = useDataFilters(signatories, 'created_at');

  // Sort data
  const { sortConfig, sortedData, sortData } = useSortData(filteredData);

  // Pagination
  const ITEMS_PER_PAGE = 20;
  const { currentPage, totalPages, paginatedData, goToPage, nextPage, prevPage } = usePagination({
    data: sortedData,
    itemsPerPage: ITEMS_PER_PAGE
  });

  // Function to display sort icon
  const getSortIcon = (columnKey: keyof CharterSignatory) => {
    if (sortConfig && sortConfig.key === columnKey) {
      return sortConfig.direction === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />;
    }
    return null;
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex justify-between items-center">
          <div>
            <CardTitle>Signataires</CardTitle>
            <CardDescription>Liste des signataires de la charte</CardDescription>
          </div>
          <ExportButtons signatories={sortedData} />
        </div>
      </CardHeader>
      <CardContent>
        <div className="mb-6 space-y-4">
          <div className="w-full md:w-1/2">
            <SearchBar 
              searchTerm={searchTerm} 
              onSearchChange={handleSearchChange} 
              placeholder="Rechercher par nom, prénom ou email..." 
            />
          </div>
          
          <DateRangeFilter
            startDate={startDate}
            endDate={endDate}
            onStartDateChange={handleStartDateChange}
            onEndDateChange={handleEndDateChange}
          />
        </div>

        <DataLoader
          isLoading={isLoading}
          error={error}
          isEmpty={sortedData.length === 0}
          emptyMessage="Aucun signataire trouvé"
        >
          <>
            <Table>
              <TableCaption>Liste des signataires de la charte</TableCaption>
              <TableHeader className="bg-primary text-white">
                <TableRow>
                  <TableHead 
                    onClick={() => sortData('first_name')}
                    className="cursor-pointer hover:bg-primary/80 transition-colors"
                  >
                    Prénom {getSortIcon('first_name')}
                  </TableHead>
                  <TableHead 
                    onClick={() => sortData('last_name')}
                    className="cursor-pointer hover:bg-primary/80 transition-colors"
                  >
                    Nom {getSortIcon('last_name')}
                  </TableHead>
                  <TableHead 
                    onClick={() => sortData('email')}
                    className="cursor-pointer hover:bg-primary/80 transition-colors"
                  >
                    Email {getSortIcon('email')}
                  </TableHead>
                  <TableHead 
                    onClick={() => sortData('phone')}
                    className="cursor-pointer hover:bg-primary/80 transition-colors"
                  >
                    Téléphone {getSortIcon('phone')}
                  </TableHead>
                  <TableHead 
                    onClick={() => sortData('created_at')}
                    className="cursor-pointer hover:bg-primary/80 transition-colors"
                  >
                    Date de signature {getSortIcon('created_at')}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedData.map((signatory) => (
                  <TableRow key={signatory.id}>
                    <TableCell>{signatory.first_name}</TableCell>
                    <TableCell>{signatory.last_name}</TableCell>
                    <TableCell>{signatory.email}</TableCell>
                    <TableCell>{signatory.phone || "—"}</TableCell>
                    <TableCell>{formatDate(signatory.created_at)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            <div className="flex justify-between items-center mt-4">
              <div className="text-sm text-muted-foreground">
                Affichage de {paginatedData.length > 0 ? (currentPage - 1) * ITEMS_PER_PAGE + 1 : 0} à {Math.min(currentPage * ITEMS_PER_PAGE, sortedData.length)} sur {sortedData.length} signataires
              </div>
              
              <TablePagination 
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={goToPage}
                onPrevPage={prevPage}
                onNextPage={nextPage}
              />
            </div>
          </>
        </DataLoader>
      </CardContent>
    </Card>
  );
};
